import { cloneDeep, each, filter, find, findIndex, keyBy, map, sortBy, uniq, without } from '@dt/fe-utils/dist/lodash'
import { makeAutoObservable, autorun, runInAction } from 'mobx'
import { storeMobx } from 'restaurant'
import { PayApi } from 'api'
import { getPlatform } from '@utils'
import { TPayScene } from './pay.mobx'

const METHOD_SORT = {
  GiftCard: 0,
  MemberCard: 1,
  BankCardOnline: 3,
  ApplePayWeb: 2,
}

export class PayChannel {
  constructor () {
    makeAutoObservable(this, {
      storeId: false
    })
  }

  /** 门店 id */
  storeId?: number

  /** 点餐场景 */
  useType = 'dc'

  /** 其他场景 mall: 商城 cz: 充值 goods: 虚拟商品购买 */
  otherUseTypes = ['mall', 'cz', 'goods']

  /** 点餐 支付渠道 */
  channels: IPayChannel[] = []

  /** 所有支付渠道 */
  allChannels: {
    [key: string]: IPayChannel[];
  } = {}

  /** 渠道帐号 */
  channelAccount: Partial<Record<TPayChannelType, Record<string, string>>> = {}

  /** 渠道方式 */
  channelMethod: Partial<Record<TPayChannelType, TPayMethodType>> = {
    GiftCard: 'GiftCard',
    MemberBalance: 'MemberCard',
    CardConnectBankCard: 'BankCardOnline',
    HeartLandBankCard: 'BankCardOnline',
    GlobalPaymentsBankCard: 'BankCardOnline',
    CardConnectApplePay: 'ApplePayWeb',
    GlobalPaymentsApplePay: 'ApplePayWeb',
    StripeApplePay: 'ApplePayWeb',
    PayPalWeb: 'PayPalWeb',
    AlipayApp: 'AlipayApp',
    WechatPayWeb: 'WechatPayWeb',
    ShengPayWechatWeb: 'WechatPayWeb',
    ShengPayAlipayWeb: 'AlipayApp',
    ShengPayWechatWebV2: 'WechatPayWeb',
    ShengPayAlipayWebV2: 'AlipayApp'
  }

  /** 排序 */
  get channelSortMap () {
    const list = this.channels?.map((v: any, index: number) => ({
      method: this.channelMethod?.[v.channelType],
      channel: v.channelType,
      sort: index,
    }))?.filter(c => c.method)
    return keyBy(list, 'method')
  }

  /** 可用的支付方式 */
  get availableMethods (): TPayMethodType[] {
    let methods = map(this.channels, x => this.channelMethod[x.channelType])
    methods = uniq(filter(methods))
    methods = sortBy(methods, x => METHOD_SORT[x])
    const ApplePaySession = (window as any).ApplePaySession
    if (!ApplePaySession) {
      methods = without(methods, 'ApplePayWeb')
    }
    const { isAlipay, isWeixin } = getPlatform()
    if (!isAlipay) {
      methods = without(methods, 'AlipayApp')
    }
    if (!isWeixin) {
      methods = without(methods, 'WechatPayWeb')
    }
    return methods
  }

  /** 可用的外部支付方式 */
  get availableExternalMethods (): TPayMethodType[] {
    return without(this.availableMethods, 'MemberCard', 'GiftCard')
  }

  /** 可使用会员卡 */
  get availableMemberCardMethod (): boolean {
    return !!find(this.availableMethods, a => a === 'MemberCard')
  }

  /** 可使用礼品卡 */
  get availableGiftCardMethod (): boolean {
    return !!find(this.availableMethods, a => a === 'GiftCard')
  }

  /** method map */
  get methodChannel (): Partial<Record<TPayMethodType, IPayChannel>> {
    const result: Partial<Record<TPayMethodType, IPayChannel>> = {}
    each(this.channels, x => {
      const method = this.channelMethod[x.channelType]
      result[method] = x
    })
    return result
  }

  /** 初始化 */
  async init (storeId: number, force?: boolean) {
    if (storeId === this.storeId && !force) {
      return
    }

    this.storeId = storeId

    const { result } = await PayApi.listPaymentTypesStoreV2({
      storeId: this.storeId,
      useTypes: [this.useType, ...this.otherUseTypes],
    }) as any as { result: { [key: string]: IPayChannel[] } }

    const channels = result[this.useType]
    const allChannels = result

    runInAction(() => {
      this.channels = channels
      this.allChannels = allChannels
    })

    each(channels, async ({ channelType, accountId }) => {
      // Paypal、Alipay、Wechat都无需账号配置。
      if (channelType === 'CardConnectBankCard' || channelType === 'HeartLandBankCard' || channelType === 'GlobalPaymentsBankCard' || channelType === 'StripeApplePay') {
        const { result: account } = await PayApi.listPaymenAccountExt({ accountId }) as any
        runInAction(() => {
          this.channelAccount[channelType] = account
        })
      }
    })
    return true
  }

  /** 切换支付渠道 */
  changeScene (scene?: TPayScene) {
    if (scene === 'giftcard_buy') {
      this.channels = this.allChannels?.cz || this.channels
    } else if (scene === 'coupons_buy') {
      this.channels = this.allChannels?.goods || this.channels
    } else {
      this.channels = this.allChannels?.dc || this.channels
    }
  }

  /** 添加支付渠道 三方券需要支付渠道 */
  changeChannels (info: IPayChannel, isDel?: boolean) {
    const copyChannels = cloneDeep(this.channels)
    if (isDel) {
      const cIndex = findIndex(this.channels, (item) => item.accountId === info.accountId)
      copyChannels.splice(cIndex, 1)
    } else {
      copyChannels.push(info)
    }
    this.channels = [...copyChannels]
  }
}

const payChannelMobx = new PayChannel()

autorun(async () => {
  const { storeId } = storeMobx.info
  if (storeId) {
    payChannelMobx.init(storeId)
  } else {
    const pathState = document.location?.pathname?.match(/^\/([^/]*)\/?/)?.[1]
    if (Config.STOREID && pathState && ['mergeqr', 'mergeqrv2', 'posCheckout', 'posPayfail'].includes(pathState)) {
      setTimeout(() => {
        payChannelMobx.init(Config.STOREID)
      }, 1000)
    }
  }
})

export default payChannelMobx
